/**
 * The detail drawer behind a row of "Offene Hinweise".
 *
 * The overview answers HOW MANY; this answers WHICH. It is a right-side sheet
 * from the design system (`components/ui/sheet`, Radix Dialog underneath), so
 * Escape, the overlay click, the focus trap and the labelled dialog role are the
 * primitive's behaviour rather than something re-implemented here.
 *
 * COUNT VS. LIST. The reconciliation response carries these warning arrays in
 * full — the overview's number is the array's length, not a server-side sample.
 * What IS capped is how many rows this drawer puts in the DOM at once: the
 * duplicate-risk bucket routinely runs to dozens, and rendering all of them on
 * open is a cost with no reader. So the footer states shown-of-total and the
 * button reveals more. The header always shows the FULL count, never the number
 * currently rendered.
 *
 * No links and no actions: this is a read-only view of a read-only mirror, and
 * the invoice detail page does not exist yet.
 */
import React, { useEffect, useState } from "react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../ui/sheet";
import {
  visibleWarningFields,
  warningRowDetail,
  warningRowTitle,
} from "./bexioWarningFields";

/** Rows rendered before the reader asks for more. */
const PAGE = 20;

export default function BexioWarningDetailDrawer({
  open,
  onOpenChange,
  category,
  items,
  currency,
}) {
  const [shown, setShown] = useState(PAGE);
  const rows = Array.isArray(items) ? items : [];

  // A different category in the same drawer starts at the top again.
  useEffect(() => {
    setShown(PAGE);
  }, [category?.key, open]);

  const visible = rows.slice(0, shown);
  const remaining = rows.length - visible.length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Wider than the primitive's default `sm:max-w-sm`: these rows carry four
          to six figures each, and 384px turns every one of them into two lines.
          Flex column so the header stays put and only the list scrolls. */}
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
      >
        <SheetHeader className="shrink-0 border-b border-border p-5 pr-12 text-left">
          <SheetTitle className="text-base">{category?.label || "Hinweis"}</SheetTitle>
          <SheetDescription>
            {/* The authoritative count: the whole array, not what is rendered. */}
            {rows.length}{" "}
            {rows.length === 1 ? "betroffener Eintrag" : "betroffene Einträge"}
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Keine Einträge in dieser Kategorie.
            </p>
          ) : (
            <ul className="space-y-3">
              {visible.map((item, i) => {
                const fields = visibleWarningFields(category?.key, item, currency);
                const detail = warningRowDetail(item);
                return (
                  <li
                    key={`${item.bexio_invoice_id ?? "x"}-${
                      item.vantio_invoice_number ?? i
                    }`}
                    className="rounded-lg border border-border p-3"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-foreground">
                        {warningRowTitle(item)}
                      </span>
                      {item.bexio_invoice_id != null ? (
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                          Bexio-ID {item.bexio_invoice_id}
                        </span>
                      ) : null}
                    </div>

                    {fields.length ? (
                      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
                        {fields.map((f) => (
                          <div key={f.label} className="min-w-0">
                            <dt className="truncate text-xs text-muted-foreground">
                              {f.label}
                            </dt>
                            <dd className="truncate text-sm tabular-nums text-foreground">
                              {f.value}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}

                    {detail ? (
                      <p className="mt-2 text-xs text-muted-foreground">{detail}</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {rows.length > PAGE ? (
          <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border p-3 text-xs text-muted-foreground">
            <span>
              {visible.length} von {rows.length} angezeigt
            </span>
            {remaining > 0 ? (
              <button
                type="button"
                onClick={() => setShown((n) => n + PAGE)}
                className="shrink-0 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Mehr anzeigen
              </button>
            ) : null}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
